import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import * as d3 from 'd3';
import { config } from '../../config/const';
import { CATEGORIES, MOCK_TOPICS } from './mockTopics';
import './KnowledgeGraph.css';

const MASTERY_CONFIG = {
  learned:   { fill: '#4CAF50', stroke: '#388E3C', label: 'Learned', icon: '✓' },
  weak:      { fill: '#FFC107', stroke: '#F57F17', label: 'Needs Practice', icon: '~' },
  available: { fill: '#E0E0E0', stroke: '#9E9E9E', label: 'Ready to Learn', icon: '' },
  locked:    { fill: '#F5F5F5', stroke: '#BDBDBD', label: 'Locked', icon: '🔒' },
};

const KnowledgeGraph = () => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedChild = location.state?.selectedChild || null;
  const childAge = selectedChild?.age || 6;
  const childName = selectedChild?.name || null;

  // Check if user is logged in
  const isLoggedIn = !!localStorage.getItem('email');

  const [selectedTopic, setSelectedTopic] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 900, height: 600 });
  const [matchingStories, setMatchingStories] = useState([]);
  const [loadingStories, setLoadingStories] = useState(false);

  // Filter topics by child's age
  const topics = MOCK_TOPICS.filter(t => childAge >= t.ageMin && childAge <= t.ageMax);

  // Build links from prerequisites
  const links = [];
  topics.forEach(topic => {
    topic.prerequisites.forEach(preId => {
      if (topics.find(t => t.id === preId)) {
        links.push({ source: preId, target: topic.id });
      }
    });
  });

  // Stats
  const stats = {
    total: topics.length,
    learned: topics.filter(t => t.mastery === 'learned').length,
    weak: topics.filter(t => t.mastery === 'weak').length,
    available: topics.filter(t => t.mastery === 'available').length,
    locked: topics.filter(t => t.mastery === 'locked').length,
  };

  // Responsive sizing
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const w = containerRef.current.clientWidth;
        setDimensions({ width: Math.max(w, 300), height: Math.max(500, w * 0.6) });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Fetch matching public stories when a topic is selected (for public/discovery mode)
  useEffect(() => {
    if (!selectedTopic || !selectedTopic.tags) {
      setMatchingStories([]);
      return;
    }

    const fetchStories = async () => {
      setLoadingStories(true);
      try {
        const baseUrl = config?.backend_url || 'http://localhost:3002';
        const response = await axios.get(`${baseUrl}/user/getStoryPublic`, {
          params: { page: 1, limit: 20 },
        });
        const allStories = response.data.stories || [];

        // Match stories by tags: story tags vs topic tags (case-insensitive)
        const topicTags = selectedTopic.tags.map(t => t.toLowerCase());
        const matched = allStories.filter(story => {
          const storyTags = (story.tag || []).map(t => t.toLowerCase().replace('#', ''));
          return storyTags.some(st =>
            topicTags.some(tt => st.includes(tt) || tt.includes(st))
          );
        });

        setMatchingStories(matched.slice(0, 5)); // Show up to 5
      } catch (error) {
        console.error('Error fetching stories:', error);
        setMatchingStories([]);
      } finally {
        setLoadingStories(false);
      }
    };

    fetchStories();
  }, [selectedTopic]);

  const handleCreateStory = useCallback((topic) => {
    if (!isLoggedIn) {
      navigate('/Login');
      return;
    }
    const prompt = `Create a fun and educational story for ${childName || 'a child'}${childAge ? ` (age ${childAge})` : ''} about ${topic.name}. Make it engaging with interesting facts that help kids learn about ${topic.name.toLowerCase()}.`;
    navigate('/create/story', {
      state: {
        selectedChild,
        suggestedPrompt: prompt,
        suggestedTopic: topic.name,
      },
    });
  }, [childName, childAge, selectedChild, navigate, isLoggedIn]);

  // d3 force simulation
  useEffect(() => {
    if (!svgRef.current) return;
    const { width, height } = dimensions;

    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height);

    const g = svg.append('g');
    const zoom = d3.zoom()
      .scaleExtent([0.3, 3])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });
    svg.call(zoom);

    const visibleTopics = activeCategory
      ? topics.filter(t => t.category === activeCategory)
      : topics;
    const visibleIds = new Set(visibleTopics.map(t => t.id));
    const visibleLinks = links.filter(l => visibleIds.has(l.source) && visibleIds.has(l.target));

    const simNodes = visibleTopics.map(t => ({ ...t }));
    const simLinks = visibleLinks.map(l => ({ source: l.source, target: l.target }));

    const simulation = d3.forceSimulation(simNodes)
      .force('link', d3.forceLink(simLinks).id(d => d.id).distance(90))
      .force('charge', d3.forceManyBody().strength(-200))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(35));

    const link = g.append('g')
      .selectAll('line')
      .data(simLinks)
      .join('line')
      .attr('class', 'kg-link')
      .attr('stroke', '#ddd')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', d => {
        const targetNode = simNodes.find(n => n.id === (typeof d.target === 'string' ? d.target : d.target.id));
        return targetNode?.mastery === 'locked' ? '4,4' : 'none';
      });

    const node = g.append('g')
      .selectAll('g')
      .data(simNodes)
      .join('g')
      .attr('class', 'kg-node')
      .attr('cursor', d => d.mastery === 'locked' ? 'not-allowed' : 'pointer')
      .on('click', (event, d) => {
        event.stopPropagation();
        if (d.mastery !== 'locked') {
          setSelectedTopic(d);
        }
      })
      .call(d3.drag()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x; d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x; d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null; d.fy = null;
        })
      );

    node.append('circle')
      .attr('r', d => d.mastery === 'learned' ? 28 : 24)
      .attr('fill', d => {
        const cat = CATEGORIES[d.category];
        if (!isLoggedIn) return '#fff'; // Public mode: all white fill
        if (d.mastery === 'learned') return cat.color;
        if (d.mastery === 'weak') return MASTERY_CONFIG.weak.fill;
        if (d.mastery === 'locked') return MASTERY_CONFIG.locked.fill;
        return '#fff';
      })
      .attr('stroke', d => {
        const cat = CATEGORIES[d.category];
        if (!isLoggedIn) return cat.color; // Public mode: category color border
        if (d.mastery === 'learned') return cat.color;
        if (d.mastery === 'weak') return MASTERY_CONFIG.weak.stroke;
        if (d.mastery === 'locked') return MASTERY_CONFIG.locked.stroke;
        return cat.color;
      })
      .attr('stroke-width', d => d.mastery === 'available' ? 2.5 : 2)
      .attr('opacity', d => (!isLoggedIn || d.mastery !== 'locked') ? 1 : 0.5);

    // Mastery icons (only for logged-in users)
    if (isLoggedIn) {
      node.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '0.35em')
        .attr('font-size', d => d.mastery === 'learned' ? '14px' : '12px')
        .attr('fill', d => d.mastery === 'learned' ? 'white' : '#666')
        .text(d => MASTERY_CONFIG[d.mastery].icon);
    }

    // Labels
    node.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', d => (d.mastery === 'learned' && isLoggedIn ? 28 : 24) + 14)
      .attr('font-size', '11px')
      .attr('font-weight', '500')
      .attr('fill', '#555')
      .text(d => d.name);

    simulation.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);
      node.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    svg.on('click', () => setSelectedTopic(null));

    return () => simulation.stop();
  }, [dimensions, topics, links, activeCategory, isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Header title depends on mode
  const pageTitle = isLoggedIn && childName
    ? `${childName}'s Learning Map`
    : 'Story Learning Map';

  const pageSubtitle = isLoggedIn
    ? (<>Explored <strong>{stats.learned}</strong> of <strong>{stats.total}</strong> topics
        {stats.weak > 0 && <> · <span className="kg-weak-count">{stats.weak} need practice</span></>}</>)
    : 'Explore topics and discover stories for kids';

  return (
    <div className="kg-page">
      <div className="kg-header">
        <div className="kg-header-left">
          <button className="kg-back-btn" onClick={() => navigate(-1)}>← Back</button>
          <div>
            <h1>{pageTitle}</h1>
            <p className="kg-subtitle">{pageSubtitle}</p>
          </div>
        </div>
      </div>

      {/* Legend — only for logged-in users */}
      {isLoggedIn && (
        <div className="kg-legend">
          {Object.entries(MASTERY_CONFIG).map(([key, cfg]) => (
            <div key={key} className="kg-legend-item">
              <span
                className="kg-legend-dot"
                style={{ background: cfg.fill, border: `2px solid ${cfg.stroke}` }}
              ></span>
              <span>{cfg.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Category filters */}
      <div className="kg-filters">
        <button
          className={`kg-filter-btn ${!activeCategory ? 'active' : ''}`}
          onClick={() => setActiveCategory(null)}
        >
          All Topics
        </button>
        {Object.entries(CATEGORIES).map(([key, cat]) => (
          <button
            key={key}
            className={`kg-filter-btn ${activeCategory === key ? 'active' : ''}`}
            style={activeCategory === key ? { background: cat.color, color: 'white' } : {}}
            onClick={() => setActiveCategory(activeCategory === key ? null : key)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Graph */}
      <div className="kg-graph-container" ref={containerRef}>
        <svg ref={svgRef}></svg>
      </div>

      {/* Topic detail panel */}
      {selectedTopic && (
        <div className="kg-detail-panel">
          <button className="kg-detail-close" onClick={() => setSelectedTopic(null)}>×</button>
          <div className="kg-detail-header">
            {isLoggedIn && (
              <span
                className="kg-detail-badge"
                style={{
                  background: MASTERY_CONFIG[selectedTopic.mastery].fill,
                  color: selectedTopic.mastery === 'learned' ? 'white' : '#333',
                }}
              >
                {MASTERY_CONFIG[selectedTopic.mastery].label}
              </span>
            )}
            <span className="kg-detail-category" style={{ color: CATEGORIES[selectedTopic.category]?.color }}>
              {CATEGORIES[selectedTopic.category]?.label}
            </span>
          </div>
          <h2>{selectedTopic.name}</h2>
          <p className="kg-detail-age">Ages {selectedTopic.ageMin}–{selectedTopic.ageMax}</p>

          {isLoggedIn && selectedTopic.prerequisites.length > 0 && (
            <div className="kg-detail-prereqs">
              <strong>Prerequisites:</strong>{' '}
              {selectedTopic.prerequisites.map(pId => {
                const p = topics.find(t => t.id === pId);
                return p ? p.name : pId;
              }).join(', ')}
            </div>
          )}

          {/* Matching stories section */}
          <div className="kg-stories-section">
            <h4 className="kg-stories-heading">
              {isLoggedIn ? 'Related Stories' : 'Stories about this topic'}
            </h4>
            {loadingStories ? (
              <p className="kg-stories-loading">Finding stories...</p>
            ) : matchingStories.length > 0 ? (
              <div className="kg-stories-list">
                {matchingStories.map(story => (
                  <div
                    key={story._id}
                    className="kg-story-card"
                    onClick={() => navigate(`/Story/${story._id}`)}
                  >
                    {story.thumbnail && (
                      <img src={story.thumbnail} alt={story.title} className="kg-story-thumb" />
                    )}
                    <div className="kg-story-info">
                      <span className="kg-story-title">{story.title}</span>
                      <span className="kg-story-meta">
                        {story.views || 0} views · {story.likes || 0} likes
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="kg-no-stories">No stories found for this topic yet.</p>
            )}
          </div>

          {/* Action button */}
          {isLoggedIn && selectedTopic.mastery !== 'locked' ? (
            <button
              className="kg-create-story-btn"
              onClick={() => handleCreateStory(selectedTopic)}
            >
              Create a story about {selectedTopic.name}
            </button>
          ) : !isLoggedIn ? (
            <button
              className="kg-create-story-btn"
              onClick={() => navigate('/Login')}
            >
              Sign in to create your own story
            </button>
          ) : null}

          {/* Mastery notes for logged-in users */}
          {isLoggedIn && selectedTopic.mastery === 'learned' && (
            <p className="kg-detail-note">
              {childName} has explored this topic! Create another story to deepen understanding.
            </p>
          )}
          {isLoggedIn && selectedTopic.mastery === 'weak' && (
            <p className="kg-detail-note kg-weak-note">
              Quiz scores suggest {childName} could benefit from another story on this topic.
            </p>
          )}
        </div>
      )}

      {/* Stats bar — only for logged-in users */}
      {isLoggedIn && (
        <div className="kg-stats-bar">
          <div className="kg-stat">
            <div className="kg-stat-number" style={{ color: '#4CAF50' }}>{stats.learned}</div>
            <div className="kg-stat-label">Learned</div>
          </div>
          <div className="kg-stat">
            <div className="kg-stat-number" style={{ color: '#FFC107' }}>{stats.weak}</div>
            <div className="kg-stat-label">Needs Practice</div>
          </div>
          <div className="kg-stat">
            <div className="kg-stat-number" style={{ color: '#9E9E9E' }}>{stats.available}</div>
            <div className="kg-stat-label">Ready to Learn</div>
          </div>
          <div className="kg-stat">
            <div className="kg-stat-number" style={{ color: '#BDBDBD' }}>{stats.locked}</div>
            <div className="kg-stat-label">Locked</div>
          </div>
        </div>
      )}

      {/* Public mode: sign-in prompt at bottom */}
      {!isLoggedIn && (
        <div className="kg-public-cta">
          <p>Sign in to track your child's learning progress and get personalized story suggestions</p>
          <button onClick={() => navigate('/Login')} className="kg-signin-btn">
            Sign in with Google
          </button>
        </div>
      )}
    </div>
  );
};

export default KnowledgeGraph;
